/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { ReactComponent as IconEdit } from "../../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconCheck } from "../../../../../../shared/assets/Icons/IconCheckSquared.svg";
import { ReactComponent as IconCancel } from "../../../../../../shared/assets/Icons/IconCrossSquare.svg";
import { ReactComponent as IconPlus } from "../../../../../../shared/assets/Icons/IconPlusNV.svg";

export interface LatencyTargetRow {
  id: string;
  hostname: string;
  interface: string;
  ipDestino: string;
}

export interface LatencyTargetsTableProps {
  latencyTargets: Record<string, Record<string, string[]>> | null;
  onUpdate: (latencyTargets: Record<string, Record<string, string[]>> | null) => void;
}

export const LatencyTargetsTable: React.FC<LatencyTargetsTableProps> = ({
  latencyTargets,
  onUpdate,
}) => {
  const [latencyRows, setLatencyRows] = useState<LatencyTargetRow[]>([]);
  const [editingLatencyRowId, setEditingLatencyRowId] = useState<string | null>(null);
  const [editingLatencyRowData, setEditingLatencyRowData] = useState<LatencyTargetRow | null>(null);

  // Initialize Latency Targets rows from form data
  useEffect(() => {
    if (latencyTargets) {
      const rows: LatencyTargetRow[] = [];
      Object.entries(latencyTargets).forEach(([hostname, interfaces]) => {
        Object.entries(interfaces).forEach(([interfaceName, ips]) => {
          ips.forEach((ip) => {
            rows.push({
              id: `latency-${Date.now()}-${Math.random()}`,
              hostname,
              interface: interfaceName,
              ipDestino: ip,
            });
          });
        });
      });
      setLatencyRows(rows);
    } else {
      setLatencyRows([]);
    }
  }, [latencyTargets]);

  const convertLatencyRowsToNested = (rows: LatencyTargetRow[]): Record<string, Record<string, string[]>> | null => {
    if (rows.length === 0) return null;
    
    const nested: Record<string, Record<string, string[]>> = {};
    rows.forEach((row) => {
      if (!nested[row.hostname]) {
        nested[row.hostname] = {};
      }
      if (!nested[row.hostname][row.interface]) {
        nested[row.hostname][row.interface] = [];
      }
      if (row.ipDestino && !nested[row.hostname][row.interface].includes(row.ipDestino)) {
        nested[row.hostname][row.interface].push(row.ipDestino);
      }
    });
    return nested;
  };

  const handleAddLatencyRow = () => {
    const newRow: LatencyTargetRow = {
      id: `latency-${Date.now()}-${Math.random()}`,
      hostname: "",
      interface: "",
      ipDestino: "",
    };
    setLatencyRows([...latencyRows, newRow]);
    setEditingLatencyRowId(newRow.id);
    setEditingLatencyRowData({ ...newRow });
  };

  const handleEditLatencyRow = (rowId: string) => {
    const row = latencyRows.find(r => r.id === rowId);
    if (row) {
      setEditingLatencyRowId(rowId);
      setEditingLatencyRowData({ ...row });
    }
  };

  const handleSaveLatencyRow = (rowId: string) => {
    if (editingLatencyRowData) {
      const updatedRows = latencyRows.map(row => 
        row.id === rowId ? editingLatencyRowData : row
      );
      setLatencyRows(updatedRows);
      setEditingLatencyRowId(null);
      setEditingLatencyRowData(null);
      
      // Convert to nested structure and update form data
      const nested = convertLatencyRowsToNested(updatedRows);
      onUpdate(nested);
    }
  };

  const handleCancelLatencyRow = () => {
    if (editingLatencyRowId && editingLatencyRowData) {
      const isEmpty = !editingLatencyRowData.hostname && 
                      !editingLatencyRowData.interface && 
                      !editingLatencyRowData.ipDestino;
      
      if (isEmpty) {
        setLatencyRows(latencyRows.filter(row => row.id !== editingLatencyRowId));
      }
    }
    setEditingLatencyRowId(null);
    setEditingLatencyRowData(null);
  };

  const handleDeleteLatencyRow = (rowId: string) => {
    const newRows = latencyRows.filter(row => row.id !== rowId);
    setLatencyRows(newRows);
    
    // Convert to nested structure and update form data
    const nested = convertLatencyRowsToNested(newRows);
    onUpdate(nested);
  };

  const handleEditingLatencyRowChange = (field: keyof LatencyTargetRow, value: string) => {
    if (editingLatencyRowData) {
      setEditingLatencyRowData({
        ...editingLatencyRowData,
        [field]: value,
      });
    }
  };

  // Define columns for GenericTable
  const latencyTableColumns: GenericTableColumn<LatencyTargetRow>[] = [
    {
      header: "HOSTNAME",
      accessor: "hostname",
      renderCell: (row) => {
        if (editingLatencyRowId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingLatencyRowData?.hostname || ""}
              onChange={(value) => handleEditingLatencyRowChange("hostname", value)}
              placeholder="Hostname"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.hostname || "-"}</span>;
      },
    },
    {
      header: "INTERFACE",
      accessor: "interface",
      renderCell: (row) => {
        if (editingLatencyRowId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingLatencyRowData?.interface || ""}
              onChange={(value) => handleEditingLatencyRowChange("interface", value)}
              placeholder="Interface"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.interface || "-"}</span>;
      },
    },
    {
      header: "IPDESTINO",
      accessor: "ipDestino",
      renderCell: (row) => {
        if (editingLatencyRowId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingLatencyRowData?.ipDestino || ""}
              onChange={(value) => handleEditingLatencyRowChange("ipDestino", value)}
              placeholder="IP Destino"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.ipDestino || "-"}</span>;
      },
    },
    {
      header: "Actions",
      renderCell: (row) => {
        if (editingLatencyRowId === row.id) {
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveLatencyRow(row.id)}
                className="flex items-center justify-center w-8 h-8 border border-semantic-border-secondary rounded text-green-600 hover:bg-green-50"
                title="Save"
              >
                <IconCheck className="w-5 h-5" />
              </button>
              <button
                onClick={handleCancelLatencyRow}
                className="flex items-center justify-center w-8 h-8 border border-semantic-border-secondary rounded text-red-600 hover:bg-red-50"
                title="Cancel"
              >
                <IconCancel className="w-5 h-5" />
              </button>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEditLatencyRow(row.id)}
              className="
              flex items-center justify-center
              border border-semantic-border-secondary
              rounded
              text-semantic-text-secondary
              hover:bg-semantic-state-active
              w-10 h-10
            "
              title="Edit"
            >
              <IconEdit className="w-10 h-10" />
            </button>
            <button
              onClick={() => handleDeleteLatencyRow(row.id)}
              className="
              flex items-center justify-center
              p-2
              border border-semantic-border-secondary
              rounded
              bg-red-50
              text-red-600
              hover:bg-red-100
              w-10 h-10"
              title="Delete"
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <GenericCard isCollapsible={false} className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-semibold text-semantic-text-primary">
          Latency Targets
        </h3>
        <GenericButton
            label={
            <div className="flex items-center gap-2">
                <IconPlus className="w-5 h-5" />
                <span>Add</span>
            </div>
            }
          variant="secondary"
          size="sm"
          onClick={handleAddLatencyRow}
        />
      </div>
      
      {latencyRows.length === 0 ? (
        <div className="text-center py-8 text-semantic-text-secondary">
          No latency targets entries. Click &quot;Add more&quot; to add one.
        </div>
      ) : (
        <GenericTable
          columns={latencyTableColumns}
          data={latencyRows}
          rowKey={(row) => row.id}
          tableClassName="bg-semantic-bg-primary"
          width="100%"
          isFooterVisible={false}
          initialRowsPerPage={10}
          rowsPerPageOptions={[5, 10, 20]}
        />
      )}
    </GenericCard>
  );
};

